/**
 * ================================================================================================
 *  THE SHELL, BUILT FROM THE APPROVED PLATFORM PROTOTYPE'S OWN STRUCTURE.
 * ================================================================================================
 *
 * Owner instruction, 2026-10-18, verbatim: *"The two ZIPs I supplied contain the approved coded
 * prototypes, not merely visual inspiration. Use them as the visual implementation reference.
 * Preserve existing behavior, permissions and data bindings while adapting the approved HTML/CSS
 * structure into the application."*
 *
 * So the element structure below is the kit's, name for name:
 *
 *     .gp-notice          the ribbon row
 *     .gp-header          a 13.24% logo segment beside a toolbar
 *       .gp-logo            the supplied asset, cropped by CSS, never edited
 *       .gp-header-content  the environment pill at the inline start, tools at the inline end
 *     .gp-layout          a 13.24% sidebar beside the content
 *       .gp-nav             the destinations
 *       .gp-content         the screen
 *
 * ── WHAT THE RIBBON SAYS, AND WHAT IT REFUSES TO SAY ────────────────────────────────────────────
 *
 * The kit's ribbon reads *"design proposal - sample data only - no connection to live data
 * sources"*. That sentence is true of the prototype and false of the product, and printing it over
 * a firm's real ledger would be a lie across the top of every screen. It is replaced by the place
 * name alone -- `סביבת בדיקות` -- per the Owner's instruction not to claim data provenance from a
 * deployment environment in either direction. The development fixture notice is a SEPARATE ribbon
 * answering a separate question, and neither speaks for the other.
 *
 * ── CONTEXT AND SIGN-OUT ARE NOT RE-IMPLEMENTED HERE ────────────────────────────────────────────
 *
 * `ContextBar` stays the only component that can change the company or the period and the only one
 * that can end a session (`contextSingleSource.test.ts` enforces the first). It is now rendered
 * INSIDE `.gp-header-content`, which is the approved placement for exactly those controls, rather
 * than copied into a second toolbar that would then drift from it.
 *
 * ── EVERY SCREEN KEEPS ITS OWN BODY ─────────────────────────────────────────────────────────────
 *
 * `titleHe` still renders a heading and `children` still render beneath it, so a screen that has
 * not been redesigned yet renders exactly as it does today, inside the approved chrome. A screen
 * that HAS been redesigned -- the Overview, as of this change -- passes `leadHe` and `actions` and
 * gets the kit's own `gp-top` composition instead.
 */

import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { ContextBar } from './ContextBar';
import { useWorkContext } from './ContextProvider';
import { fixturesEnabled, FIXTURE_NOTICE_HE } from '../api/fixtures';
import { EnvironmentRibbon } from './EnvironmentRibbon';
import { LocaleFlag } from './LocaleFlag';
import { ShellNav } from './ShellNav';
import { MarketRail } from './MarketRail';
import { UpdatesBanner } from '../components/UpdatesBanner';
import { callOperation } from '../api/operations';
import { useServerData } from '../api/useServerData';
import type { MarketCardView } from '../api/contract';
import { AreaBar } from './AreaBar';
import { RecentlyUsed } from './RecentlyUsed';
import { visibleDestinations } from './destinations';
import { AboutSystem } from './AboutSystem';
import { SupportAssistant } from '../support/SupportAssistant';
import { useDeployment } from './EnvironmentProvider';
import { useLocale } from '../i18n/LocaleProvider';
import { DIRECTION, LOCALES, LOCALE_NAME } from '../i18n/catalogue';
import logoUrl from '../assets/glpoint-logo.jpeg';

export interface AppShellProps {
  /** 09 §9.1 V4: a noun phrase in office Hebrew. If it cannot be named, it is not clear enough to ship. */
  readonly titleHe: string;
  /** The kit's `top(title, lead, action)` second argument: one line under the title. */
  readonly leadHe?: string;
  /** The kit's third argument: the controls that belong beside the title, at the inline end. */
  readonly actions?: ReactNode;
  /** Names the screen for the kit's `main[data-screen=…]` geometry rules. */
  readonly screenId?: string;
  /**
   * WHAT SITS IN THE RAIL, UNDER "RECENTLY USED". **Owner, 2026-10-29.**
   *
   *     *"נתוני השוק ... יהיו באזור הצד השמאלי, מתחת ל„שימוש לאחרונה”"*
   *
   * The rail is the shell's, so the shell renders it; a screen hands over what belongs in it. The
   * first attempt put these readings at the inline end of the CONTENT area instead, which looks
   * adjacent and is a different place -- *"ולא בצד שמאל של מרכז דף הבית."*
   *
   * TENANT ONLY, like the rail itself. A platform session has no rail and anything passed here is
   * not rendered, which is the same rule `RecentlyUsed` already follows.
   */
  readonly rail?: ReactNode;
  /**
   * ── IN THE HEADER, UNDER THE CONTEXT ROW AND ABOVE THE BLUE LINE. **Owner, 2026-11-01.** ────
   *
   *     *"לגבי לשכת רואי חשבון אני רוצה שזה יהיה פס גלילה, דיברנו על זה, מתחת לסביבת הארגון
   *     מעל הקו הכחול."*
   *
   * THREE PLACES IN ONE DAY, AND THIS IS THE ONE HE DREW. It was a card in the rail; then the
   * head of the content area; then above the greeting, which is why this slot exists at all. He
   * has now named the position exactly: `סביבת הארגון` is the pill `ContextBar` draws in
   * `.gp-header-content`, and `הקו הכחול` is `.gp-header`'s own `border-block-end: 3px solid
   * var(--gp-blue)`. Between those two is INSIDE the header element, as a full-width second grid
   * row.
   *
   * IT IS NOT A SECOND `children`. One screen passes it, it has a fixed place, and a screen that
   * passes nothing renders nothing — no wrapper, no gap and no rule, which is what keeps the
   * header exactly one row high on every other screen.
   */
  readonly banner?: ReactNode;
  readonly backToParent?: { readonly labelHe: string; readonly href: string };
  /*
   * ── THE PROVENANCE LINE IS GONE. **2026-10-28, the Owner's third time asking.** ──────────────
   *
   * *"אני לא רוצה לראות הסברים בתוך המערכת. המערכת צריכה להיות נקייה."* — after *"המלל המיותר
   * שמוצג בדפים ... המערכת צריכה להיות מקצועית עם כותרות לפעולות מבלי לתת הסברים מיותרים"* and
   * *"הסברים ארוכים בעזרה מתקפלת"*.
   *
   * `provenanceHe` was a paragraph at the foot of the content area, rendered on EVERY visit to a
   * screen whether or not anybody was asking, on thirty screens. Each sentence was true and well
   * meant — where a figure came from, what a status does not imply — and together they were the
   * product explaining itself to a professional who already knows his own office.
   *
   * It is removed as a MECHANISM and not screen by screen, because a prop that exists is a prop the
   * next screen uses. What it carried has three proper homes: a fact a reader needs in order to act
   * is beside the control, in a few words; a longer explanation belongs in collapsible help or in
   * the assistant, which is where he moved it; and a caveat about a NUMBER belongs in the cell that
   * prints the number, which 09 §5.1 already requires.
   *
   * `no-standing-prose.test.ts` is what keeps it gone.
   */
  /**
   * THIS SCREEN RENDERS NO HEBREW LITERAL. **Section 7, 2026-10-26.**
   *
   * Set it and the content region follows the ACTIVE locale instead of being marked Hebrew, and
   * the Hebrew-only notice is not rendered — there is no gap to state.
   *
   * **IT IS A CLAIM THE BUILD CHECKS.** `translated-screens.test.ts` reads every screen that
   * passes this prop and fails if a single Hebrew string literal survives in it, comments
   * stripped. A boolean nobody verifies is exactly the half-translated screen the Owner forbade:
   * *"אין להשאיר מסך מעורב בעברית ובאנגלית"*.
   *
   * `titleHe` and `leadHe` keep their names on purpose. Renaming the props across forty screens to
   * make six of them honest would be a refactor pretending to be a translation; what matters is
   * what the screen PASSES, and the control reads that.
   */
  readonly translated?: boolean;
  readonly children: ReactNode;
}

export function AppShell({
  titleHe,
  leadHe,
  actions,
  screenId,
  rail,
  banner,
  backToParent,
  translated = false,
  children,
}: AppShellProps): JSX.Element {
  const ctx = useWorkContext();
  const { environment, release } = useDeployment();
  const { t, locale, setLocale } = useLocale();
  const [aboutOpen, setAboutOpen] = useState(false);

  const initials = (ctx.session?.displayName ?? '').trim().slice(0, 2);

  /*
   * ── THE MARKET READ, ONCE, FOR BOTH THE STRIP AND THE RAIL. **Owner, 2026-11-01.** ─────────
   *
   *     *"אתה עובר בין מסכים, הוויג׳טים לא אמורים להשתנות."*
   *
   * Both were the home screen's and both therefore disappeared on every other screen: the
   * readings came through the `rail` prop and the strip through `banner`. They are furniture,
   * so the shell renders them — and the shell makes the ONE call that feeds both, because
   * `user.market.read` answers every domain at once and two components reading it separately
   * would be two requests for one answer.
   *
   * IT IS THE CUSTOMER SURFACE ONLY. The platform console has no rail and no strip; without
   * the guard this would issue a tenant read on every console screen.
   *
   * THE HOOK IS ABOVE NO EARLY RETURN. `useServerData` has no `enabled`, so the guard is on the
   * CALL: a platform session resolves to an empty answer and issues nothing.
   */
  const isTenant = ctx.surface === 'TENANT';
  const readMarket = useCallback(
    () =>
      isTenant
        ? callOperation('user.market.read', {})
        : Promise.resolve({ cards: [] as readonly MarketCardView[] }),
    [isTenant],
  );
  const marketRead = useServerData(readMarket).result;

  return (
    <div className="app-shell gp" data-surface={ctx.surface} data-dir={DIRECTION[locale]}>
      {/* WHERE YOU ARE. It replaces the kit's demo-data ribbon and says nothing about the data. */}
      <EnvironmentRibbon environment={environment} />

      {/*
        AND WHERE THE NUMBERS CAME FROM, when that is actually known. A development build serves
        fixtures, which IS a provenance fact, so it gets its own ribbon in its own colour. The
        banner sits above the layout row: it was briefly inside it, where a flex row laid it out as
        an unreadable vertical strip.
      */}
      {fixturesEnabled() ? (
        <div className="fixture-ribbon" role="status">
          {FIXTURE_NOTICE_HE}
        </div>
      ) : null}

      <header className="gp-header">
        <div className="gp-logo">
          <div className="gp-brand-image">
            {/*
              The asset the Owner supplied with the approved design, byte-identical in both kits.
              `alt` is the product name because the mark IS the name -- a screen reader that said
              "logo" would be describing the file rather than reading the word on it.
            */}
            <img src={logoUrl} alt="GL-POINT" />
          </div>
        </div>

        <div className="gp-header-content">
          {/*
            THE CONTEXT CONTROLS AT THE TOOLBAR INLINE START, where the kit puts its estate pill.

            It is `ContextBar`, unchanged and still the only component that can change the company
            or the period and the only one that can end a session. On a platform screen it renders
            the surface badge and the utilities alone -- 09 s3.3 makes the company slot ABSENT on
            that surface -- which is precisely the kit toolbar.

            An earlier draft put a second pill here naming the surface. `ContextBar` already draws
            that badge, so the header said the same thing twice in two different shapes.
          */}
          {/*
            `backToParent` IS NO LONGER PASSED HERE. **Section 1, 2026-10-27.**

            It used to be drawn by `ContextBar`, inside `.context-bar__controls`, beside the period
            selector — so the button moved whenever that selector was absent (09 s5.2) and again
            with the breadcrumb's width. The Owner: *"כותרות ופעולות מחליפות מיקום"*.

            It is now the first child of `AreaBar`, below, which is the one place in the product
            that draws it. Two components drawing one control is the duplication this round was
            told to avoid, so the old arm was REMOVED rather than left as a fallback.
          */}
          <ContextBar />

          <div className="gp-header-tools">
            {ctx.session === null ? null : (
              <>
                {/*
                  ── THE NAMES LEFT THIS TOOLBAR. **Section 1, 2026-10-28.** ────────────────────

                  The office name and the person's name were here, and the same two names were in
                  the breadcrumb at the other end of the same header, and a third copy of both was
                  on the home page. Owner: *"המידע מפוזר וכפול"*.

                  `ContextBar` carries all three now -- organisation, signed-in person, selected
                  company -- each with its label, in one row. What stays here is the avatar, which
                  is a mark and not a name, and the utilities.
                */}
                <span className="gp-avatar" aria-hidden="true" title={ctx.session.displayName}>
                  {initials}
                </span>
                <span className="gp-header-divider" />
              </>
            )}

            {/*
              THE LANGUAGE, Master §28 — Hebrew default RTL, English toggle LTR, in the approved
              place. Each language is named IN that language, never "Hebrew" written in English to
              a Hebrew reader. Choosing one rewrites the document's `lang` AND `dir` from a single
              value, so the two can never disagree.
            */}
            <span className="gp-locale">
              {LOCALES.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={option === locale}
                  onClick={() => setLocale(option)}
                  /*
                    THE NAME STAYS THE LANGUAGE. **2026-10-25.** The Owner asked to SEE flags, and a
                    flag names a country rather than a language — Hebrew is not Israel and English
                    is not only the United States. So the flag is drawn and the accessible name is
                    still `עברית` / `English`, in its own language as Master §28 requires.
                  */
                  aria-label={LOCALE_NAME[option]}
                  title={LOCALE_NAME[option]}
                >
                  <LocaleFlag locale={option} />
                </button>
              ))}
            </span>
            <span className="gp-header-divider" />

            {/* ABOUT SYSTEM, reachable from every screen because this shell wraps every screen. */}
            <button type="button" onClick={() => setAboutOpen(true)}>
              {t('about.title')}
            </button>
          </div>
        </div>

        {/*
          ── THE RUNNING STRIP, AS THE THIRD THING IN THE HEADER. **Owner, 2026-11-01.** ────────

          *"מתחת לסביבת הארגון מעל הקו הכחול."* A full-width second grid row, so it spans the
          logo column as well as the content column and the blue border stays beneath it.

          IT IS MARKED `lang="he" dir="rtl"` ITSELF. Everything else in this header is chrome and
          follows the chosen language; these are a publisher's Hebrew headlines, and in an
          English session they would otherwise sit in an LTR line with their punctuation
          resolving in an order nobody chose. The content region below does this for a screen's
          body; the header has no such wrapper, so the strip carries it.
        */}
        {isTenant ? (
          <div className="gp-header-strip" lang="he" dir="rtl">
            <UpdatesBanner result={marketRead} />
          </div>
        ) : null}
      </header>

      <div className="gp-layout">
        {ctx.surface === null ? null : (
          <ShellNav
            surface={ctx.surface}
            capabilities={ctx.session?.capabilities ?? []}
            currentPath={typeof window === 'undefined' ? '' : window.location.pathname}
          />
        )}

        <main
          className="gp-content"
          {...(screenId === undefined ? {} : { 'data-screen': screenId })}
        >
          {/*
            ══ THE AREA BAR. **Section 1, 2026-10-27.** ═══════════════════════════════════

            Owner: *"הסרגל יהיה באותו מיקום אנכי ובאותו גובה בכל המסכים"* — so it is rendered
            HERE, at one point in the shell, and that point is above everything a screen can vary:
            above the heading, above the actions, and above the Hebrew-only notice (which is itself
            conditional, and would otherwise have pushed the bar down on an English session alone).

            It is OUTSIDE the `lang`/`dir` wrapper beneath it on purpose. The bar is chrome, like
            the rail and the toolbar: its words follow the chosen language, while the screen's body
            is marked Hebrew until that screen itself is translated.

            `window.location.pathname` rather than `useLocation`: this shell is mounted by the
            jsdom suites and by the UI harness WITHOUT a router, and `useLocation` throws outside
            one. Every screen renders its own `AppShell`, so the value is read once per navigation,
            which is exactly when it changes. `ShellNav` and `RecentlyUsed` already read it so.
          */}
          <AreaBar
            currentPath={typeof window === 'undefined' ? '' : window.location.pathname}
            surface={ctx.surface}
            capabilities={ctx.session?.capabilities ?? []}
            {...(backToParent === undefined ? {} : { backToParent })}
          />

          {/*
            ── THE TAB STRIP IS GONE FROM HERE. **§3, 2026-10-25.** ───────────────────────────────

            `WorkTabs` drew the customer kit's strip at exactly this point, which is what put a band
            of tab-shaped controls immediately above the page heading and its primary action. The
            Owner's words: *"זה לא נראה ככפתור זה נראה כמו טאב"* — about `הקמת חברה`, which is a
            button, nine pixels under a row of tabs.

            The same list now renders in the rail as `בשימוש לאחרונה` (`RecentlyUsed`, mounted by
            `ShellNav`), where a list of places belongs and where it cannot be mistaken for the
            action beside the title.
          */}
          {/*
            ── THE SCREEN'S BODY IS HEBREW, AND AN ENGLISH SESSION IS TOLD SO. **§6, 2026-10-23.** ─

            Every prop this shell takes for screen text is named `…He`, and every screen passes
            Hebrew. Switching the language therefore rewrote `documentElement.dir` to `ltr` and
            left that Hebrew sitting inside an LTR document: left-aligned, with its punctuation and
            its LTR codes resolving in an order nobody chose, and with no explanation.

            That is the half-state §6 names, and it has two halves to fix. Direction is markup, and
            `lang="he" dir="rtl"` on the region is simply the truth about what is inside it — a
            bidi algorithm and a screen reader both need it, and it costs nothing in a Hebrew
            session because it agrees with the document. The notice is the other half: a gap that
            is stated is a gap somebody can plan, and one that is merely rendered is a defect.

            IT IS IN THE SHELL AND NOT IN FOUR SCREENS. Every screen in this product is authored
            this way, so a per-screen flag would be a flag set on all of them — and the one
            forgotten would be the one that strands Hebrew silently. A screen that acquires English
            text loses the notice by gaining its own translated props, not by remembering to.
          */}
          {locale === 'he' || translated ? null : (
            <section className="state-block" data-tone="stale" role="status">
              <h2 className="state-block__title">{t('shell.hebrewOnly.title')}</h2>
              <p className="state-block__body">{t('shell.hebrewOnly.body')}</p>
            </section>
          )}

          {/*
            A TRANSLATED SCREEN IS MARKED AS WHAT IT IS. For every other screen the region stays
            `lang="he" dir="rtl"`, which is simply the truth about what is inside it — a bidi
            algorithm and a screen reader both need it, and it costs nothing in a Hebrew session.
          */}
          <div lang={translated ? locale : 'he'} dir={translated ? DIRECTION[locale] : 'rtl'}>
            <div className="gp-top">
              <div>
                <h1>{titleHe}</h1>
                {leadHe === undefined ? null : <div className="gp-muted">{leadHe}</div>}
              </div>
              {actions === undefined ? null : <div className="gp-inline">{actions}</div>}
            </div>

            {children}

            {/*
              THE ASSISTANT. **Section 5, 2026-10-26.** In the SHELL, so the button is on every
              screen and no screen has to remember it -- the same reasoning the Hebrew-only notice
              above is placed here for. It reads `screenId` and answers about the screen it is
              standing on; a screen with no entry gets a stated absence rather than a paragraph.
            */}
            <SupportAssistant screenId={screenId} />

          </div>
        </main>

        {/*
          ── בשימוש לאחרונה, ON THE PHYSICAL LEFT. **Section 5, 2026-10-25.** ────────────────────

          Owner, three times and more exactly each time, ending with: *"השימוש לאחרונה צריך להיות
          בצד שמאל ולא למטה בהמשך"*. The first attempt put it inside the rail — and in Hebrew the
          rail is the grid's inline-START, so it rendered bottom-RIGHT.

          IT IS THE THIRD GRID TRACK, and that is the whole mechanism. Grid tracks run in the INLINE
          direction, so a third one sits at inline-END: the physical left in a Hebrew document, with
          the main navigation still on the right — the Priority arrangement he attached. An English
          session mirrors the entire shell and it moves with it, which is correct. A hard-coded
          `left` would have been right in Hebrew and wrong in English.

          TENANT ONLY, like the rail it came out of: it is the customer workspace's furniture and
          the platform console does not grow one.
        */}
        {/*
          ONE GRID CHILD, HOLDING BOTH. `.gp-layout` is a THREE-COLUMN grid, and the first attempt
          put the readings beside `RecentlyUsed` as a fourth child -- which placed them on an
          implicit second row at the opposite edge, measured at left 1236. The wrapper keeps the
          grid at three columns and makes the rail a column with two things in it.

          `RecentlyUsed` returns `null` when nothing has been opened yet -- a new office on its
          first day -- so the readings are its SIBLING and not its child, and do not disappear
          with it. When both are absent the wrapper is empty and `:empty` hides it.
        */}
        {ctx.surface === 'TENANT' ? (
          <div className="gp-rail">
            <RecentlyUsed
              destinations={visibleDestinations('TENANT', ctx.session?.capabilities ?? [])}
              currentPath={typeof window === 'undefined' ? '' : window.location.pathname}
            />
            <MarketRail result={marketRead} />
          </div>
        ) : null}
      </div>

      {aboutOpen ? (
        <AboutSystem
          environment={environment}
          release={release}
          onClose={() => setAboutOpen(false)}
        />
      ) : null}
    </div>
  );
}
